/**
 * SubjectGlyph
 * Renders a line-art glyph for a given subject. Used as the large watermark
 * on course covers and as the small subject chip icon.
 */
import React from 'react';
import { GlyphKey } from '../../utils/courseVisuals';

interface SubjectGlyphProps {
  glyph: GlyphKey;
  className?: string;
  strokeWidth?: number;
}

const PATHS: Record<GlyphKey, React.ReactNode> = {
  biology: (
    <>
      <path d="M7 3c0 5 10 8 10 13a5 5 0 0 1-10 0" />
      <path d="M17 3c0 5-10 8-10 13a5 5 0 0 0 10 0" />
      <path d="M8.5 7h7M8 11h8M8.5 15h7" />
    </>
  ),
  chemistry: (
    <>
      <path d="M9 3h6M10 3v6l-5 8a2 2 0 0 0 1.8 3h10.4A2 2 0 0 0 19 17l-5-8V3" />
      <path d="M7.5 14h9" />
    </>
  ),
  physics: (
    <>
      <circle cx="12" cy="12" r="1.6" />
      <ellipse cx="12" cy="12" rx="10" ry="4.2" />
      <ellipse cx="12" cy="12" rx="10" ry="4.2" transform="rotate(60 12 12)" />
      <ellipse cx="12" cy="12" rx="10" ry="4.2" transform="rotate(120 12 12)" />
    </>
  ),
  math: (
    <>
      <path d="M5 5h14" />
      <path d="M8 5c0 6-1.5 14-3 14" />
      <path d="M14 5c0 6 1.5 14 3 14" />
      <path d="M6 12h12" />
    </>
  ),
  cs: (
    <>
      <path d="M8 8l-4 4 4 4" />
      <path d="M16 8l4 4-4 4" />
      <path d="M13 6l-2 12" />
    </>
  ),
  history: (
    <>
      <path d="M4 8l8-4 8 4-8 4-8-4z" />
      <path d="M6 10v5c0 1 2.7 2.5 6 2.5s6-1.5 6-2.5v-5" />
      <path d="M20 8v5" />
    </>
  ),
  literature: (
    <>
      <path d="M12 5C10 3.5 6.5 3.5 4 4.5v13c2.5-1 6-1 8 .5 2-1.5 5.5-1.5 8-.5v-13C17.5 3.5 14 3.5 12 5z" />
      <path d="M12 5v13" />
    </>
  ),
  art: (
    <>
      <path d="M12 3a9 9 0 0 0 0 18c1.4 0 2-.9 2-1.8 0-1.2-1-1.6-1-2.7 0-.8.7-1.5 1.6-1.5H17a4 4 0 0 0 4-4c0-4.4-4-8-9-8z" />
      <circle cx="7.5" cy="11" r="1" />
      <circle cx="11" cy="7.5" r="1" />
      <circle cx="15.5" cy="8.5" r="1" />
    </>
  ),
  business: (
    <>
      <path d="M4 20h16" />
      <path d="M6 20V11M11 20V6M16 20v-6" />
      <path d="M4 10l5-4 4 3 7-6" />
    </>
  ),
  earth: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3.5 9h17M3.5 15h17" />
      <path d="M12 3c2.8 3 2.8 15 0 18M12 3c-2.8 3-2.8 15 0 18" />
    </>
  ),
  psychology: (
    <>
      <path d="M9.5 4A4.5 4.5 0 0 0 5 8.5c0 .8-1 1.4-1 2.5s1 1.6 1 2.4c0 2 1.6 3.6 3.6 3.6H9v2.5" />
      <path d="M9.5 4A4.5 4.5 0 0 1 18 6c1.2.4 2 1.5 2 2.8s-.7 2.2-1.7 2.6c.2 2.4-1.6 4.6-4 4.6" />
      <path d="M9.5 4v13" />
    </>
  ),
  music: (
    <>
      <path d="M9 18V6l10-2v12" />
      <circle cx="6" cy="18" r="3" />
      <circle cx="16" cy="16" r="3" />
    </>
  ),
  health: (
    <>
      <path d="M12 21C6 17 3 13 3 8.5A4.5 4.5 0 0 1 12 6a4.5 4.5 0 0 1 9 2.5C21 13 18 17 12 21z" />
      <path d="M12 9v5M9.5 11.5h5" />
    </>
  ),
  engineering: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2 2M17 17l2 2M19 5l-2 2M7 17l-2 2" />
    </>
  ),
  academic: (
    <>
      <path d="M12 4L2 9l10 5 10-5-10-5z" />
      <path d="M6 11v5c0 1.5 2.7 3 6 3s6-1.5 6-3v-5" />
      <path d="M22 9v5" />
    </>
  ),
};

const SubjectGlyph: React.FC<SubjectGlyphProps> = ({ glyph, className, strokeWidth = 1.5 }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {PATHS[glyph]}
  </svg>
);

export default SubjectGlyph;
